import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import tourOptionsApi from '@/features/dashboard/tourOptions/api/tourOptionsApi.ts';
import { tourOptionsApiQueryKeys } from '@/features/dashboard/tourOptions/api/tourOptionsApi.queryKeys.ts';
import tourParticipantCustomFieldsApi from '@/features/dashboard/tourCustomFields/api/tourParticipantCustomFieldsApi.ts';
import { tourParticipantCustomFieldsApiQueryKeys } from '@/features/dashboard/tourCustomFields/api/tourParticipantCustomFieldsApi.queryKeys.ts';
import tourInsurancesApi from '@/features/dashboard/tourInsurances/api/tourInsurancesApi.ts';
import { tourInsurancesApiQueryKeys } from '@/features/dashboard/tourInsurances/api/tourInsurancesApi.queryKeys.ts';
import { getTransportQueryOptions } from '@/features/dashboard/tourTransport/hooks/useGetTransport.ts';
import useGetBookingDetails from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import useGetBookingBillDto from '@/features/dashboard/bookings/hooks/useGetBookingBill.ts';
import useGetBookingParticipantSelections from '@/features/dashboard/bookings/hooks/useGetBookingParticipantSelections.ts';
import useGetBookingTransactions from '@/features/dashboard/bookings/hooks/useGetBookingTransactions.ts';
import { BookingBill } from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { bookingBillDtoMapper } from '@/features/dashboard/bookingChanges/types/BookingBillDto.mapper.ts';
import { bookingTransactionMapper } from '@/features/dashboard/bookingChanges/types/BookingTransactionListDto.mapper.ts';

/**
 * Rachunek rezerwacji zebrany ze wszystkich źródeł — jedno wejście dla ekranu
 * uczestnika, zapisującego i rezerwacji.
 *
 * **Osiem żądań, niezależnie od liczby uczestników**: trzy o rezerwację (dane,
 * księga, wpłaty), cztery o katalog wyjazdu (pola niestandardowe, opcje,
 * transport, ubezpieczenia) i jedno o wybory kompletu osób. Wybory szły dotąd
 * po jednym uczestniku, czterema odczytami na osobę — rezerwacja z trójką
 * kosztowała 19 żądań, z piętnastką 67. Odczyty per uczestnik zostają nietknięte
 * dla ekranu jednej osoby.
 */
const useBookingBill = (bookingId: string | undefined) => {
  const bookingQuery = useGetBookingDetails(bookingId);
  const billQuery = useGetBookingBillDto(bookingId);
  const transactionsQuery = useGetBookingTransactions(bookingId);
  const selectionsQuery = useGetBookingParticipantSelections(bookingId);

  const tourId = bookingQuery.data?.tourId;

  const customFieldsQuery = useQuery({
    queryKey: tourParticipantCustomFieldsApiQueryKeys.allForTour(tourId ?? ''),
    queryFn: () => tourParticipantCustomFieldsApi.getAllFieldsForTour(tourId!),
    enabled: !!tourId,
    staleTime: 1000 * 60,
  });

  const tourOptionsQuery = useQuery({
    queryKey: tourOptionsApiQueryKeys.byTourId(tourId ?? ''),
    queryFn: () => tourOptionsApi.getAll(tourId!),
    enabled: !!tourId,
    staleTime: 1000 * 60,
  });

  const transportQuery = useQuery(getTransportQueryOptions(tourId));

  const tourInsurancesQuery = useQuery({
    queryKey: tourInsurancesApiQueryKeys.byTourId(tourId ?? ''),
    queryFn: () => tourInsurancesApi.get(tourId!),
    enabled: !!tourId,
    staleTime: 1000 * 60,
  });

  const ready =
    !!bookingQuery.data &&
    !!billQuery.data &&
    !!transactionsQuery.data &&
    !!selectionsQuery.data &&
    !!customFieldsQuery.data &&
    !!tourOptionsQuery.data &&
    !!tourInsurancesQuery.data &&
    !transportQuery.isPending;

  const data: BookingBill | undefined = useMemo(() => {
    if (!ready) {
      return undefined;
    }
    return bookingBillDtoMapper.toBookingBill({
      booking: bookingQuery.data!,
      bill: billQuery.data!,
      customFields: customFieldsQuery.data!,
      tourOptions: tourOptionsQuery.data!,
      transport: transportQuery.data ?? null,
      tourInsurances: tourInsurancesQuery.data!,
      selections: bookingBillDtoMapper.selectionsByParticipant(
        selectionsQuery.data!,
      ),
      payments: transactionsQuery.data!.content.map(
        bookingTransactionMapper.toBillPayment,
      ),
    });
  }, [
    ready,
    bookingQuery.data,
    billQuery.data,
    transactionsQuery.data,
    selectionsQuery.data,
    customFieldsQuery.data,
    tourOptionsQuery.data,
    transportQuery.data,
    tourInsurancesQuery.data,
  ]);

  const allQueries = [
    bookingQuery,
    billQuery,
    transactionsQuery,
    selectionsQuery,
    customFieldsQuery,
    tourOptionsQuery,
    transportQuery,
    tourInsurancesQuery,
  ];

  return {
    data,
    isLoading: !data && allQueries.some((query) => query.isPending),
    isError: allQueries.some((query) => query.isError),
  };
};

export default useBookingBill;
